import { InfiniteComboBox } from "@/ds/custom/InfiniteComboBox";
import { LoadingState } from "@/ds/custom/LoadingState";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Checkbox } from "@/ds/shadcn/components/ui/checkbox";
import { Input } from "@/ds/shadcn/components/ui/input";
import { Label } from "@/ds/shadcn/components/ui/label";
import { Spinner } from "@/ds/shadcn/components/ui/spinner";
import { cn } from "@/ds/utils";
import type { CollectionModel } from "@/features/collections/api/models/CollectionModel";
import { useCollection } from "@/features/collections/api/useCollection";
import { useCollectionDelete } from "@/features/collections/api/useCollectionDelete";
import { useCollections } from "@/features/collections/api/useCollections";
import { useCollectionUpdate } from "@/features/collections/api/useCollectionUpdate";
import { useGoalCollection } from "@/features/collections/api/useGoalCollection";
import { useSetGoalCollection } from "@/features/collections/api/useSetGoalCollection";
import { ModalConfirm } from "@/features/common/components/ModalConfirm";
import { CollectionImage } from "@/features/common/components/CollectionImage";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { SplitScreen, SplitScreenLeftPanel, SplitScreenRightPanel } from "@/features/common/components/SplitScreen";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { Limit } from "@/lib/common/Limit";
import { useNotifyMutationSuccess } from "@/lib/common/useNotifyMutationSuccess";
import { useTitle } from "@/lib/common/useTitle";
import { collectionArts } from "@/lib/media/collectionArts";
import { useCallback, useEffect, useState, type FC } from "react";
import { useNavigate, useParams } from "react-router";

const artGradient = "linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 38.02%)";

export const TabCollectionSettings: FC = () => {
	const params = useParams();
	const collectionId = params.collectionId as string;
	const navigate = useNavigate();

	useTitle("Collection Settings");

	const collection = useCollection(collectionId);
	const updateCollection = useCollectionUpdate();
	useNotifyMutationSuccess(updateCollection, "Collection Saved");

	const deleteCollection = useCollectionDelete();
	useNotifyMutationSuccess(deleteCollection, "Collection Deleted");

	const goalCollection = useGoalCollection(collectionId);

	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [valued, setValued] = useState(false);
	const [image, setImage] = useState(0);
	const [imageSelectorVisible, setImageSelectorVisible] = useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	useEffect(() => {
		if (!collection.data) {
			return;
		}

		setName(collection.data.name);
		setDescription(collection.data.description);
		setValued(collection.data.valued);
		setImage(collection.data.image);
	}, [collection.data]);

	useEffect(() => {
		if (!deleteCollection.isSuccess) {
			return;
		}

		navigate("/collections");
	}, [deleteCollection.isSuccess]);

	const saveDisabled = !collection.data || updateCollection.isPending || (
		name === collection.data.name &&
		description === collection.data.description &&
		valued === collection.data.valued &&
		image === collection.data.image
	);

	const onSave = useCallback(() => {
		if (!collection.data) {
			return;
		}

		updateCollection.mutate({
			requestModel: {
				name,
				description,
				valued,
				pinned: collection.data.pinned,
				image
			},
			id: collectionId
		});
	}, [name, description, valued, image, collection.data, collectionId, updateCollection]);

	const onImageSelect = useCallback((index: number) => {
		setImage(index);
		setImageSelectorVisible(false);
	}, []);

	const onDeleteConfirm = useCallback(() => {
		deleteCollection.mutate(collectionId);
	}, [collectionId, deleteCollection]);

	if (collection.isError) {
		return (
			<div className="p-3">
				<ErrorDisplay error={collection.error} />
			</div>
		);
	}

	if (collection.isPending) {
		return <LoadingState />;
	}

	return (
		<>
			<HeaderBarTools>
				<Button
					variant="outline"
					disabled={saveDisabled}
					onClick={onSave}>
					{
						updateCollection.isPending
							? <Spinner />
							: <>Save Changes</>
					}
				</Button>
			</HeaderBarTools>

			<SplitScreen
				fixed
				visible={imageSelectorVisible}
				onVisibleChange={setImageSelectorVisible}>
				<SplitScreenLeftPanel>
					<Limit>
						<div className="flex flex-col gap-8 p-3">
							<div className="flex flex-col gap-4">
								<div className="type-section">Collection Details</div>

								<div className="flex flex-col gap-4">
									<div
										className="aspect-9/3 cursor-pointer flex items-center justify-center rounded-md bg-cover bg-center"
										style={{ backgroundImage: `${artGradient}, url('${collectionArts[image]}')` }}
										onClick={() => setImageSelectorVisible(true)}>
										<span className="text-white font-bold" style={{ textShadow: "0 0 10px #000" }}>
											Edit Image
										</span>
									</div>

									<div className="flex flex-col gap-2">
										<Label>Name</Label>
										<Input
											placeholder="Collection name"
											value={name}
											onChange={e => setName(e.target.value)} />
									</div>

									<div className="flex flex-col gap-2">
										<Label>Description</Label>
										<Input
											placeholder="Collection description"
											value={description}
											onChange={e => setDescription(e.target.value)} />
									</div>

									<label className="flex items-center gap-2 text-sm cursor-pointer">
										<Checkbox
											checked={valued}
											onCheckedChange={checked => setValued(!!checked)} />
										Include the value of this collection in the dashboard summary
									</label>
								</div>

								<ErrorDisplay error={updateCollection.error} />
							</div>

							<div className="flex flex-col gap-4">
								<div>
									<div className="type-section">Goal Collection</div>
									<div className="type-meta">
										Set another collection as the goal for this collection to track your progress towards completing it.
									</div>
								</div>

								<ErrorDisplay error={goalCollection.error} />

								{
									goalCollection.isLoading
										? <LoadingState />
										: goalCollection.data?.goalCollection
											? <TabCollectionSettingsGoalCollectionCurrent collectionId={collectionId} goalCollectionId={goalCollection.data.goalCollection.goalCollectionId} />
											: <TabCollectionSettingsGoalCollectionPicker collectionId={collectionId} initialValue={null} />
								}
							</div>

							<div className="flex flex-col gap-4">
								<div className="type-section text-destructive">Danger Zone</div>

								<div className="border border-destructive rounded-lg p-4 flex flex-col gap-3">
									<div>
										<div className="type-item">Delete Collection</div>
										<div className="type-meta">
											Permanently delete this collection and all of its transactions. This action cannot be undone.
										</div>
									</div>

									<Button
										variant="destructive"
										className="w-full @md:w-auto"
										onClick={() => setDeleteModalOpen(true)}>
										Delete Collection
									</Button>
								</div>
							</div>
						</div>
					</Limit>
				</SplitScreenLeftPanel>

				<SplitScreenRightPanel>
					<div className="grid grid-cols-3 gap-3 p-3">
						{
							collectionArts.map((art, i) => (
								<div
									key={i}
									className={cn(
										"aspect-5/3 cursor-pointer bg-cover bg-center rounded-sm",
										i === image && "ring-2 ring-primary ring-offset-1"
									)}
									style={{ backgroundImage: `url('${art}')` }}
									onClick={() => onImageSelect(i)} />
							))
						}
					</div>
				</SplitScreenRightPanel>
			</SplitScreen>

			{
				collection.data &&
				<ModalConfirm
					open={deleteModalOpen}
					onOpenChange={setDeleteModalOpen}
					onConfirm={onDeleteConfirm}
					title="Delete Collection"
					description={`Are you sure you want to delete "${collection.data.name}"? All transactions in this collection will be deleted permanently${collection.data.isGoalCollection ? " and it will be removed as the goal from your other collections." : "."}`}
					confirmText="Delete"
					confirmDestructive={true}
					error={deleteCollection.error}
					isLoading={deleteCollection.isPending} />
			}
		</>
	);
};

interface TabCollectionSettingsGoalCollectionCurrentProps {
	collectionId: string;
	goalCollectionId: string;
}

const TabCollectionSettingsGoalCollectionCurrent: FC<TabCollectionSettingsGoalCollectionCurrentProps> = props => {
	const {
		collectionId,
		goalCollectionId
	} = props;

	const currentGoalCollection = useCollection(goalCollectionId);

	if (currentGoalCollection.isLoading) {
		return <LoadingState />;
	}

	return <TabCollectionSettingsGoalCollectionPicker collectionId={collectionId} initialValue={currentGoalCollection.data ?? null} />;
};

interface TabCollectionSettingsGoalCollectionPickerProps {
	collectionId: string;
	initialValue: CollectionModel | null;
}

const TabCollectionSettingsGoalCollectionPicker: FC<TabCollectionSettingsGoalCollectionPickerProps> = props => {
	const {
		collectionId,
		initialValue
	} = props;

	const collections = useCollections("Alphabetical", null);
	const setGoalCollection = useSetGoalCollection();
	useNotifyMutationSuccess(setGoalCollection, "Goal Collection Updated");
	const [selected, setSelected] = useState(initialValue);

	useEffect(() => {
		setSelected(initialValue);
	}, [initialValue]);

	const onChange = useCallback((value: CollectionModel | null) => {
		setSelected(value);

		if (!value) {
			return;
		}

		setGoalCollection.mutate({
			collectionId,
			requestModel: { goalCollectionId: value.collectionId }
		});
	}, [collectionId, setGoalCollection]);

	const onRemove = useCallback(() => {
		setSelected(null);
		setGoalCollection.mutate({
			collectionId,
			requestModel: { goalCollectionId: null }
		});
	}, [collectionId, setGoalCollection]);

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-col gap-3 @md:flex-row">
				<InfiniteComboBox
					value={selected}
					onChange={onChange}
					forQuery={collections}
					filter={x => x.collectionId !== collectionId}
					optionComponent={TabCollectionSettingsGoalCollectionOption}
					placeholder="Select collection"
					disabled={setGoalCollection.isPending}
					className="w-full @md:flex-1" />

				<Button
					variant="outline"
					className="w-full @md:w-auto"
					onClick={onRemove}
					disabled={!selected || setGoalCollection.isPending}>
					Remove
				</Button>
			</div>

			<ErrorDisplay error={setGoalCollection.error} />
		</div>
	);
};

const TabCollectionSettingsGoalCollectionOption: FC<CollectionModel> = props => {
	const {
		name,
		image
	} = props;

	return (
		<div className="flex items-center gap-3">
			<CollectionImage image={image} className="size-5 aspect-square rounded-sm bg-cover" />
			<span className="truncate">{name}</span>
		</div>
	);
};
